/**
 * Sharing for review and watching together: which comments belong to a
 * review link's conversation, what media a shared asset plays, and how a
 * viewer follows a leader's playhead (Sync).
 */
import { isFolderLinkedKey } from './sources';

// ---------------------------------------------------------------------------
// Review link conversations
// ---------------------------------------------------------------------------

export interface ThreadComment {
  id: string;
  parentId?: string | null;
  /** Review link the conversation belongs to; '' = the team's own. */
  linkId?: string;
  /** '' for comments made by guests through a link. */
  authorUserId: string;
}

/**
 * The comments one review link's guests see: the link's own conversation,
 * plus guest comments from before links had their own conversations (they
 * stay visible everywhere, as they were), and every reply to those.
 */
export function linkThreadComments<C extends ThreadComment>(comments: readonly C[], linkId: string): C[] {
  const roots = new Set(comments.filter((c) => !c.parentId && (c.linkId === linkId || (!c.linkId && c.authorUserId === ''))).map((c) => c.id));
  return comments.filter((c) => roots.has(c.id) || (!!c.parentId && roots.has(c.parentId)));
}

/** The conversation a comment is in: its link's id, or '' for the team's (replies follow their thread). */
export function commentThreadId(c: ThreadComment, byId: ReadonlyMap<string, ThreadComment>): string {
  const root = c.parentId ? (byId.get(c.parentId) ?? c) : c;
  return root.linkId ?? '';
}

// ---------------------------------------------------------------------------
// Assets shared for review
// ---------------------------------------------------------------------------

export type ReviewMediaKind = 'video' | 'audio' | 'image';

/**
 * What a review link to an asset plays: the browser-friendly proxy when there
 * is one, else the original. Null for files that cannot be reviewed (fonts,
 * LUTs) or that are not ready yet.
 */
export function assetReviewMedia(a: { kind: string; status: string; storageKey: string; proxyKey: string; hasVideo?: boolean }): { kind: ReviewMediaKind; key: string } | null {
  if (a.status !== 'ready') return null;
  const kind: ReviewMediaKind | null = a.kind === 'image' ? 'image' : a.kind === 'audio' ? 'audio' : a.kind === 'video' ? (a.hasVideo === false ? 'audio' : 'video') : null;
  if (!kind) return null;
  // An original kept on a NAS is never served: only its proxy.
  const key = a.proxyKey || (isFolderLinkedKey(a.storageKey) ? '' : a.storageKey);
  return key ? { kind, key } : null;
}

// ---------------------------------------------------------------------------
// Approvals on shared assets
// ---------------------------------------------------------------------------

/**
 * Where an asset of the media library is in its review. Without an
 * asset_review row it is 'editing' (no badge). A review opens when a link that
 * allows approvals is made (or a member decides, or starts a new round), and
 * runs in rounds like a project's: a new round starts from 'in_review' again.
 */
export const ASSET_REVIEW_STATUSES = ['editing', 'in_review', 'approved', 'changes_requested'] as const;
export type AssetReviewStatus = (typeof ASSET_REVIEW_STATUSES)[number];

export interface AssetDecisionLike {
  round: number;
  /** approved | changes_requested */
  decision: string;
}

/**
 * The status of an open round from its decisions (one per reviewer: deciding
 * again replaces the earlier one). Anyone asking for changes wins over
 * approvals, whoever decides last, so a request is never hidden by another
 * reviewer's approval; otherwise one approval approves it. Unlike a project's
 * round, a guest's approval counts: approving media through a link is what
 * clients are asked to do.
 */
export function assetReviewStatus(round: number, decisions: readonly AssetDecisionLike[]): AssetReviewStatus {
  if (round <= 0) return 'editing';
  const current = decisions.filter((d) => d.round === round);
  if (current.some((d) => d.decision === 'changes_requested')) return 'changes_requested';
  if (current.some((d) => d.decision === 'approved')) return 'approved';
  return 'in_review';
}

/** One decision per reviewer and round: members by user id, guests by the name they gave. */
export function decisionReviewerKey(d: { userId: string; name: string }): string {
  return d.userId ? `u:${d.userId}` : `g:${d.name.trim().toLowerCase()}`;
}

// ---------------------------------------------------------------------------
// Sync: following a leader's playhead
// ---------------------------------------------------------------------------

export interface FollowState {
  /** Leader's playhead (µs) as last published. */
  leaderUs: number;
  leaderPlaying: boolean;
  /** Milliseconds since that playhead arrived (it has moved on meanwhile while playing). */
  sinceMs: number;
  localUs: number;
  localPlaying: boolean;
  /** One frame (µs): paused playheads closer than this count as equal. */
  frameUs: number;
}

export type FollowAction = { play: boolean; seekUs?: number };

/** Drift tolerated while both play before seeking (presence arrives a few times a second). */
export const FOLLOW_DRIFT_US = 400_000;

/**
 * What a follower does to match the leader: start or stop playing, and seek
 * when the playheads are apart (paused: by more than half a frame; playing:
 * by more than FOLLOW_DRIFT_US, aiming where the leader is by now).
 */
export function followAction(s: FollowState): FollowAction {
  if (s.leaderPlaying) {
    const target = s.leaderUs + Math.max(0, Math.min(s.sinceMs, 2000)) * 1000;
    const drift = Math.abs(s.localUs - target);
    return !s.localPlaying || drift > FOLLOW_DRIFT_US ? { play: true, seekUs: Math.max(0, Math.round(target)) } : { play: true };
  }
  return Math.abs(s.localUs - s.leaderUs) > s.frameUs / 2 ? { play: false, seekUs: Math.max(0, s.leaderUs) } : { play: false };
}
